import { useMutation, useQuery } from "@tanstack/react-query"
import {
  listResources,
  getResource,
  createResource,
  updateResource,
  checkConnection,
  validateConnection,
  listSchemas,
  listResourceSchemas,
  listContainers,
  listContainerFields,
  updateField,
  getFieldPossibleValues,
  updateFieldPossibleValues,
  resyncResource,
  deleteResource,
  listSemanticTypes,
  generateContainerDescription,
  updateContainer,
} from "@/lib/http/resources"

export const useListResources = (refetchOnMount: boolean | "always" = true) =>
  useQuery({ queryKey: ["resources"], queryFn: listResources, refetchOnMount })

export const useGetResource = (resourceId: string) =>
  useQuery({
    queryKey: ["resources", resourceId],
    queryFn: () => getResource(resourceId),
    enabled: !!resourceId,
  })

export const useCreateResource = (options: {
  onSuccess?: () => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: (data: { name: string; description?: string; type: string; meta: any }) => createResource(data),
    onSuccess: options.onSuccess,
    onSettled: options.onSettled,
    onError: options.onError,
  })

export const useUpdateResource = (options: {
  onSuccess?: () => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: ({
      resourceId,
      data,
    }: {
      resourceId: string
      data: { name: string; description?: string; type: string; meta: any }
    }) => updateResource(resourceId, data),
    onSuccess: options.onSuccess,
    onSettled: options.onSettled,
    onError: options.onError,
  })

export const useCheckConnection = (options: {
  onSuccess?: (success: boolean) => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: (data: { resource_id: string }) => checkConnection(data),
    onSuccess: (response: { success: boolean }) => options.onSuccess?.(response.success),
    onSettled: options.onSettled,
    onError: options.onError,
  })

export const useValidateConnection = (options: {
  onSuccess?: (success: boolean) => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: (data: { resource_type: string; meta: any }) => validateConnection(data),
    onSuccess: (response: { success: boolean }) => options.onSuccess?.(response.success),
    onSettled: options.onSettled,
    onError: options.onError,
  })

export const useListSchemas = (options: {
  onSuccess?: (schemas: string[]) => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: (data: { resource_type: string; meta: any }) => listSchemas(data),
    onSuccess: (response: { schemas: string[] }) => options.onSuccess?.(response.schemas || []),
    onSettled: options.onSettled,
    onError: options.onError,
  })

export const useListResourceSchemas = (options: {
  onSuccess?: (schemas: string[]) => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: (resourceId: string) => listResourceSchemas(resourceId),
    onSuccess: (response: { schemas: string[] }) => options.onSuccess?.(response.schemas || []),
    onSettled: options.onSettled,
    onError: options.onError,
  })

export const useListContainers = (resourceId: string) =>
  useQuery({
    queryKey: ["resources/containers", resourceId],
    queryFn: () => listContainers(resourceId),
    enabled: !!resourceId,
  })

export const useListContainerFields = (resourceId: string, containerId: string) =>
  useQuery({
    queryKey: ["resources/container-fields", resourceId, containerId],
    queryFn: () => listContainerFields(resourceId, containerId),
    enabled: !!resourceId && !!containerId,
  })

export const useUpdateField = () =>
  useMutation({
    mutationFn: ({
      resourceId,
      containerId,
      fieldId,
      description,
      display_name,
      visibility,
      data_type,
      semantic_type,
    }: {
      resourceId: string
      containerId: string
      fieldId: string
      description: string
      display_name: string
      visibility: string
      data_type: string
      semantic_type: string
    }) =>
      updateField(resourceId, containerId, fieldId, {
        description,
        display_name,
        visibility,
        data_type,
        semantic_type,
      }),
  })

export const useGetFieldPossibleValues = (resourceId: string, containerId: string, fieldId: string) =>
  useQuery({
    queryKey: ["resources/field-possible-values", resourceId, containerId, fieldId],
    queryFn: () => getFieldPossibleValues(resourceId, containerId, fieldId),
    enabled: !!resourceId && !!containerId && !!fieldId,
  })

export function useSemanticTypes() {
  return useQuery({
    queryKey: ["semantic-types"],
    queryFn: () => listSemanticTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  })
}

export const useUpdateFieldPossibleValues = () =>
  useMutation({
    mutationFn: ({
      resourceId,
      containerId,
      fieldId,
      possibleValues,
    }: {
      resourceId: string
      containerId: string
      fieldId: string
      possibleValues: any[]
    }) => updateFieldPossibleValues(resourceId, containerId, fieldId, possibleValues),
  })

export const useGenerateContainerDescription = (resourceId: string, containerId: string) =>
  useQuery({
    queryKey: ["resources/containers/description", resourceId, containerId],
    queryFn: () => generateContainerDescription(resourceId, containerId),
    enabled: !!resourceId && !!containerId,
  })

export const useUpdateContainer = (options: {
  onSuccess?: () => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: ({
      resourceId,
      containerId,
      data,
    }: {
      resourceId: string
      containerId: string
      data: { description: string }
    }) => updateContainer(resourceId, containerId, data),
    onSuccess: options.onSuccess,
    onSettled: options.onSettled,
    onError: options.onError,
  })

export const useResyncResource = () =>
  useMutation({
    mutationFn: (resourceId: string) => resyncResource(resourceId),
  })

export const useDeleteResource = (options: {
  onSuccess?: () => void
  onError?: (error: any) => void
  onSettled?: () => void
}) =>
  useMutation({
    mutationFn: (resourceId: string) => deleteResource(resourceId),
    onSuccess: options.onSuccess,
    onSettled: options.onSettled,
    onError: options.onError,
  })
